// pages/reports/SupplierReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
//import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    ChevronUp,
    RotateCcw,
    DollarSign,
    TrendingUp,
    TrendingDown,
    PieChart,

    Truck,
    Package,
    CreditCard,
    RefreshCw,

    Search,

    Phone,
    Mail
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const SupplierReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [searchTerm, setSearchTerm] = useState('');

    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };

    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };

            const result = await reportActions.getSupplierReport(params);

            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // Filter suppliers by search
    const getFilteredSuppliers = () => {
        if (!reportData?.supplier_breakdown) return [];
        if (!searchTerm) return reportData.supplier_breakdown;

        return reportData.supplier_breakdown.filter(supplier =>
            supplier.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            supplier.phone?.includes(searchTerm) ||
            supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    // Render filter bar
    const renderFilterBar = () => {
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Search Supplier</label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, phone, email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;

        const { summary } = reportData;

        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Suppliers</p>
                                    <h3 className="mb-0 text-primary">
                                        {summary.total_suppliers}
                                    </h3>
                                    <small className="text-muted">Active suppliers</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <Truck size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Purchases</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(summary.total_purchases_amount)}
                                    </h3>
                                    <small className="text-muted">{summary.total_purchases} purchases</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Paid</p>
                                    <h3 className="mb-0 text-info">
                                        {reportActions.formatCurrency(summary.total_paid)}
                                    </h3>
                                    <small className="text-muted">Amount paid</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Due</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(summary.total_due)}
                                    </h3>
                                    <small className="text-muted">Pending payments</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'suppliers', label: 'Supplier Details', icon: <Truck size={16} /> },
            { id: 'products', label: 'Products by Supplier', icon: <Package size={16} /> },
            { id: 'payments', label: 'Payment Methods', icon: <CreditCard size={16} /> }
        ];

        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };

    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;

        const filteredSuppliers = getFilteredSuppliers();
        const topSuppliers = filteredSuppliers.slice(0, 5);

        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Supplier Overview</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Active Suppliers</td>
                                        <td className="text-end"><strong>{reportData.summary.total_suppliers}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Total Purchases (Period)</td>
                                        <td className="text-end"><strong>{reportData.summary.total_purchases}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Total Purchase Amount</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(reportData.summary.total_purchases_amount)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Average Purchase Value</td>
                                        <td className="text-end">
                                            <strong>
                                                {reportData.summary.total_purchases > 0
                                                    ? reportActions.formatCurrency(reportData.summary.total_purchases_amount / reportData.summary.total_purchases)
                                                    : reportActions.formatCurrency(0)}
                                            </strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Top Suppliers</h6>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Supplier</th>
                                            <th className="text-end">Purchases</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topSuppliers.map((supplier, idx) => (
                                            <tr key={idx}>
                                                <td>{supplier.supplier_name}</td>
                                                <td className="text-end">{supplier.purchase_count}</td>
                                                <td className="text-end">{reportActions.formatCurrency(supplier.total_purchases)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render suppliers tab
    const renderSuppliersTab = () => {
        const filteredSuppliers = getFilteredSuppliers();

        if (filteredSuppliers.length === 0) {
            return (
                <div className="alert alert-info">
                    No suppliers found matching your search.
                </div>
            );
        }

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Supplier Performance Details</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Supplier</th>
                                    <th>Contact</th>
                                    <th className="text-end">Purchases</th>
                                    <th className="text-end">Total Amount</th>
                                    <th className="text-end">Paid</th>
                                    <th className="text-end">Due</th>
                                    <th>Avg. Value</th>
                                    <th>Last Purchase</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSuppliers.map((supplier, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{supplier.supplier_name}</strong>
                                        </td>
                                        <td>
                                            {supplier.phone && (
                                                <div><small><Phone size={12} /> {supplier.phone}</small></div>
                                            )}
                                            {supplier.email && (
                                                <div><small><Mail size={12} /> {supplier.email}</small></div>
                                            )}
                                        </td>
                                        <td className="text-end">{supplier.purchase_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(supplier.total_purchases)}</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(supplier.total_paid)}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(supplier.total_due)}</td>
                                        <td className="text-end">
                                            {supplier.purchase_count > 0
                                                ? reportActions.formatCurrency(supplier.total_purchases / supplier.purchase_count)
                                                : '-'}
                                        </td>
                                        <td>{reportActions.formatDate(supplier.last_purchase_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render products tab
    const renderProductsTab = () => {
        if (!reportData?.product_breakdown || reportData.product_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No product data available for the selected period.
                </div>
            );
        }

        // Group products by supplier
        const productsBySupplier = {};
        reportData.product_breakdown.forEach(item => {
            if (!productsBySupplier[item.supplier_id]) {
                productsBySupplier[item.supplier_id] = {
                    supplier_name: item.supplier_name,
                    products: []
                };
            }
            productsBySupplier[item.supplier_id].products.push(item);
        });

        return (
            <div className="accordion" id="supplierProductsAccordion">
                {Object.values(productsBySupplier).map((supplier, idx) => (
                    <div className="accordion-item mb-3" key={idx}>
                        <h2 className="accordion-header">
                            <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapse${idx}`}
                            >
                                <strong>{supplier.supplier_name}</strong>
                                <span className="badge bg-primary ms-2">
                                    {supplier.products.length} products
                                </span>
                            </button>
                        </h2>
                        <div id={`collapse${idx}`} className="accordion-collapse collapse">
                            <div className="accordion-body">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th className="text-end">Purchases</th>
                                            <th className="text-end">Quantity</th>
                                            <th className="text-end">Total Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {supplier.products.map((product, pIdx) => (
                                            <tr key={pIdx}>
                                                <td>{product.product_name}</td>
                                                <td>{product.sku}</td>
                                                <td className="text-end">{product.purchase_count}</td>
                                                <td className="text-end">{product.total_quantity}</td>
                                                <td className="text-end">{reportActions.formatCurrency(product.total_cost)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render payments tab
    const renderPaymentsTab = () => {
        if (!reportData?.payment_breakdown || reportData.payment_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No payment data available for the selected period.
                </div>
            );
        }

        // Group payments by supplier
        const paymentsBySupplier = {};
        reportData.payment_breakdown.forEach(item => {
            if (!paymentsBySupplier[item.supplier_id]) {
                paymentsBySupplier[item.supplier_id] = {
                    supplier_name: item.supplier_name,
                    payments: []
                };
            }
            paymentsBySupplier[item.supplier_id].payments.push(item);
        });

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Payment Methods by Supplier</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Supplier</th>
                                    <th>Payment Method</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.payment_breakdown.map((payment, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{payment.supplier_name}</strong>
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">
                                                {payment.payment_method?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="text-end">{payment.payment_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(payment.total_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Supplier Report...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Supplier Report</h4>
                            <h6>Analyze supplier performance and purchases</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    <div>
                                        <strong>Total Suppliers:</strong> {reportData?.summary.total_suppliers || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Bar */}
                {renderFilterBar()}

                {/* Summary Cards */}
                {renderSummaryCards()}

                {/* Tabs */}
                {renderTabs()}

                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'suppliers' && renderSuppliersTab()}
                {activeTab === 'products' && renderProductsTab()}
                {activeTab === 'payments' && renderPaymentsTab()}
            </div>
        </div>
    );
};

export default SupplierReport;